import React, { useCallback, useEffect, useState } from 'react';
import { Banknote, ExternalLink, Loader, CheckCircle, Clock, AlertCircle, FileText, Download, ArrowUpRight, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { paymentsAPI } from '../services/PaymentApi';
import RequestPayoutModal from './PaymentRequestPayoutModal';
import TransactionsTable from './PaymentTransactionsTable';
import FeeAgreementCard from './PaymentFeeAgreementCard';

const money = (n) => `$${Number(n || 0).toFixed(2).replace(/\.00$/, '')}`;

/**
 * Connecting your own Stripe account, so entry fees are paid to you directly.
 *
 * Deliberately not a gate. Anyone can charge for a tournament or program with
 * nothing set up here — Kynaix collects and holds the money for them. This
 * screen is how they stop being owed and start being paid automatically, and
 * the balance below is the argument for bothering.
 *
 * Owner-agnostic on purpose: the same component serves an individual, an
 * academy and a squad, because the flow is identical and only the owner differs.
 */
export default function PayoutsSettings({ ownerType, ownerId, returnPath }) {
  const { theme } = useTheme();
  const [status, setStatus] = useState(null);
  const [balance, setBalance] = useState(null);
  const [lines, setLines] = useState([]);
  const [newLineCount, setNewLineCount] = useState(0);
  const [lastStatementNumber, setLastStatementNumber] = useState(null);
  const [statements, setStatements] = useState([]);
  const [requests, setRequests] = useState([]);
  const [showRequest, setShowRequest] = useState(false);
  const [busyRequest, setBusyRequest] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [issuing, setIssuing] = useState(false);

  const load = useCallback(async () => {
    if (!ownerId) return;
    try {
      const [s, b, l, st, pr] = await Promise.all([
        paymentsAPI.getConnectStatus(ownerType, ownerId),
        // Only a host's own balance is readable; anything wider is finance's.
        paymentsAPI.getBalances({ hostType: ownerType, hostId: ownerId }).catch(() => ({ balances: [] })),
        paymentsAPI.getStatementLines(ownerType, ownerId).catch(() => ({ lines: [] })),
        paymentsAPI.listStatements(ownerType, ownerId).catch(() => ({ statements: [] })),
        paymentsAPI.listPayoutRequests(ownerType, ownerId).catch(() => ({ requests: [] })),
      ]);
      setStatus(s);
      setBalance(b.balances?.[0] || null);
      setLines(l.lines || []);
      setNewLineCount(l.newLineCount ?? (l.lines || []).length);
      setLastStatementNumber(l.lastStatementNumber || null);
      setStatements(st.statements || []);
      setRequests(pr.requests || []);
    } catch (err) {
      toast.error(err?.message || 'Could not load payout settings');
    } finally {
      setLoading(false);
    }
  }, [ownerType, ownerId]);

  useEffect(() => { load(); }, [load]);

  /**
   * Coming back from Stripe's hosted onboarding.
   *
   * The message is driven by the STATUS WE JUST READ, not by the fact that a
   * redirect happened. Stripe returns the user here whether they finished or
   * abandoned half way, so an unconditional "connected!" would be a lie in the
   * case that most needs the truth.
   *
   * The flag is stripped from the URL afterwards, or every later refresh of
   * this page would announce it again.
   */
  useEffect(() => {
    if (loading || !status) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect') !== 'return') return;

    if (status.state === 'active') {
      toast.success('Stripe connected — your payments now go straight to your account');
    } else if (status.state === 'incomplete' || status.state === 'pending') {
      toast('Stripe still needs a few details before it can pay you', { icon: '⏳' });
    } else {
      toast('Setup was not completed. Kynaix keeps collecting for you until it is.', { icon: 'ℹ️' });
    }

    params.delete('connect');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [loading, status]);

  const startOnboarding = async () => {
    setWorking(true);
    try {
      const { url } = await paymentsAPI.startConnectOnboarding({ ownerType, ownerId, returnPath });
      // Stripe hosts the form; we only ever hand over and get an id back.
      window.location.assign(url);
    } catch (err) {
      toast.error(err?.message || 'Could not start setup');
      setWorking(false);
    }
  };

  const consented = !!status?.selfBillingConsentAt;

  const giveConsent = async () => {
    try {
      const { consentedAt } = await paymentsAPI.giveSelfBillingConsent(ownerType, ownerId);
      setStatus(s => ({ ...s, selfBillingConsentAt: consentedAt }));
    } catch (err) {
      toast.error(err?.message || 'Could not save that');
    }
  };

  const generate = async () => {
    setIssuing(true);
    try {
      const res = await paymentsAPI.generateStatement(ownerType, ownerId);
      toast.success(res.emailed
        ? `Statement ${res.number} sent to ${res.emailedTo.join(' and ')}`
        : `Statement ${res.number} created`);
      await load();
    } catch (err) {
      toast.error(err?.message || 'Could not generate the statement');
    } finally {
      setIssuing(false);
    }
  };

  const [opening, setOpening] = useState(null);

  const openStatement = async (number) => {
    setOpening(number);
    try {
      const { html } = await paymentsAPI.getStatementDocument(ownerType, ownerId, number);
      // Rendered from a blob rather than linked: the document never becomes a
      // URL that works without being signed in.
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      const win = window.open(url, '_blank', 'noopener');
      if (!win) toast.error('Allow pop-ups to view statements');
      // Give the tab a moment to load before dropping the object URL.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err?.message || 'Could not open that statement');
    } finally {
      setOpening(null);
    }
  };

  // A payout request is money in flight, so it belongs in the transaction
  // list beside the entries it draws down — not hidden in a separate panel.
  const openRequest = requests.find(r => ['draft', 'pending', 'paid'].includes(r.status));

  const requestAction = async (id, fn, successMessage) => {
    setBusyRequest(id);
    try {
      await fn(id);
      toast.success(successMessage);
      await load();
    } catch (err) {
      toast.error(err?.message || 'That did not work');
    } finally {
      setBusyRequest(null);
    }
  };

  const openInvoice = async (id) => {
    setBusyRequest(id);
    try {
      const { base64, contentType, number } = await paymentsAPI.getPayoutInvoice(id);
      const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: contentType }));
      if (!window.open(url, '_blank', 'noopener')) toast.error('Allow pop-ups to view the invoice');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err?.message || 'Could not open the invoice');
    } finally {
      setBusyRequest(null);
    }
  };

  /**
   * Every path that marks a payment received can be interrupted — a closed tab,
   * an undelivered webhook. This asks Stripe directly instead of waiting to be
   * told, which is the only way those ever get corrected.
   */
  const runAudit = async () => {
    setAuditing(true);
    setAuditResult(null);
    try {
      const res = await paymentsAPI.runAudit(ownerType, ownerId);
      setAuditResult(res);
      if (res.settled.length) {
        toast.success(`${res.settled.length} payment${res.settled.length === 1 ? '' : 's'} found in Stripe and marked paid`);
        await load();
      } else if (res.needsReview.length) {
        toast('Some payments need a closer look — see below.', { icon: '⚠️' });
      } else {
        toast.success('Everything matches Stripe');
      }
    } catch (err) {
      toast.error(err?.message || 'Could not check with Stripe');
    } finally {
      setAuditing(false);
    }
  };

  const openDashboard = async () => {
    setWorking(true);
    try {
      const { url } = await paymentsAPI.getConnectDashboardLink(ownerType, ownerId);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast.error(err?.message || 'Could not open Stripe');
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size={22} className="animate-spin text-blue-500" />
      </div>
    );
  }

  const state = status?.state || 'not_connected';
  const owed = balance?.owed ?? 0;

  const STATES = {
    not_connected: {
      Icon: Banknote, tone: theme.textMuted,
      title: 'Payments are collected by Kynaix for you',
      body: 'You can charge for tournaments and programs right now — nothing here is required. Connect your own account and Stripe pays you automatically instead.',
      cta: 'Set up payouts',
    },
    incomplete: {
      Icon: AlertCircle, tone: 'text-amber-600',
      title: 'Stripe needs a few more details',
      body: 'Your setup was started but not finished. Until it is, Kynaix keeps collecting on your behalf.',
      cta: 'Finish setup',
    },
    pending: {
      Icon: Clock, tone: 'text-amber-600',
      title: 'Stripe is verifying your details',
      body: 'This usually completes within a day. Kynaix keeps collecting until it does — nothing is lost in the meantime.',
      cta: 'Review details',
    },
    active: {
      Icon: CheckCircle, tone: 'text-green-600',
      title: 'You are paid directly',
      body: status?.payoutLast4
        ? `Entry fees go to your account ending ${status.payoutLast4}.`
        : 'Entry fees are paid straight to your bank account.',
      cta: null,
    },
  };

  const { Icon, tone, title, body, cta } = STATES[state] || STATES.not_connected;

  return (
    <div className="space-y-5">
      <div>
        <h3 className={`text-lg font-bold ${theme.text}`}>Payouts</h3>
        <p className={`text-sm ${theme.textMuted} mt-1`}>
          How you get paid for tournaments and programs you host.
        </p>
      </div>

      {/* The agreement leads, because online payments cannot be switched on
          until it is signed — finding that out from a 409 on the Features
          screen would be a worse way to learn it. */}
      <FeeAgreementCard
        ownerType={ownerType}
        ownerId={ownerId}
        connectState={state}
        onConnect={startOnboarding}
      />

      {/* Money being held. This is the reason to connect, so it leads. */}
      {owed > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
          <Banknote size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
              Kynaix is holding {money(owed)} for you
            </p>
            <p className="text-sm mt-0.5 text-blue-800 dark:text-blue-200">
              {state === 'active'
                ? 'Collected before your account was connected — it will be paid out separately.'
                : 'Connect your account and future entry fees are paid to you automatically.'}
            </p>
          </div>
          {!openRequest && (
            <button
              onClick={() => setShowRequest(true)}
              className={`px-3 py-2 ${theme.buttonPrimary} rounded-lg text-sm font-semibold whitespace-nowrap flex-shrink-0`}
            >
              Request payout
            </button>
          )}
        </div>
      )}

      {/* An in-flight request, with whatever it is waiting on. Each state is
          waiting on a different person, so each says who. */}
      {openRequest && (
        <div className={`${theme.card} rounded-xl p-4`}>
          <div className="flex items-start gap-3">
            <ArrowUpRight size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-semibold ${theme.text}`}>
                Payout of {money(openRequest.amount)} — {
                  openRequest.status === 'draft' ? 'awaiting your confirmation'
                  : openRequest.status === 'pending' ? 'awaiting payment from Kynaix'
                  : 'sent — confirm when it arrives'
                }
              </p>
              {openRequest.invoiceNumber && (
                <p className={`text-xs ${theme.textMuted} mt-0.5`}>
                  Invoice {openRequest.invoiceNumber}
                  {openRequest.paidReference && ` · ref ${openRequest.paidReference}`}
                </p>
              )}

              {/* Set the expectation while they are waiting, not only when
                  they ask. A payout that is "sent" can still be days away. */}
              {['pending', 'paid'].includes(openRequest.status) && (
                <p className={`text-xs ${theme.textMuted} mt-1.5`}>
                  {openRequest.status === 'paid'
                    ? 'Sent by your usual payment method — it normally arrives within 1–2 weeks.'
                    : 'Once processed, payment is sent by your usual payment method and normally reaches you within 1–2 weeks.'}
                </p>
              )}

              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={() => openInvoice(openRequest.id)}
                  disabled={busyRequest === openRequest.id}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 border ${theme.border} ${theme.text} rounded-lg text-xs font-semibold disabled:opacity-50`}
                >
                  <Download size={12} /> View invoice
                </button>

                {openRequest.status === 'draft' && (
                  <>
                    <button
                      onClick={() => requestAction(openRequest.id, paymentsAPI.confirmPayoutInvoice, 'Invoice confirmed')}
                      disabled={busyRequest === openRequest.id}
                      className={`px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-xs font-semibold disabled:opacity-50`}
                    >
                      Confirm invoice
                    </button>
                    <button
                      onClick={() => requestAction(openRequest.id, paymentsAPI.cancelPayoutRequest, 'Request cancelled')}
                      disabled={busyRequest === openRequest.id}
                      className={`px-3 py-1.5 text-xs font-semibold ${theme.textMuted} hover:text-red-600 disabled:opacity-50`}
                    >
                      Cancel
                    </button>
                  </>
                )}

                {openRequest.status === 'paid' && (
                  <button
                    onClick={() => requestAction(openRequest.id, paymentsAPI.markPayoutReceived, 'Thanks — marked as received')}
                    disabled={busyRequest === openRequest.id}
                    className={`px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-xs font-semibold disabled:opacity-50`}
                  >
                    Received
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`${theme.card} rounded-xl p-5`}>
        <div className="flex items-start gap-3">
          <Icon size={20} className={`${tone} flex-shrink-0 mt-0.5`} />
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-semibold ${theme.text}`}>{title}</p>
            <p className={`text-sm ${theme.textSecondary} mt-1`}>{body}</p>

            {/* Say what Stripe is actually waiting on rather than "incomplete". */}
            {state === 'incomplete' && status?.requirements?.length > 0 && (
              <ul className={`mt-3 text-xs ${theme.textMuted} list-disc pl-4 space-y-0.5`}>
                {status.requirements.slice(0, 5).map((r) => (
                  <li key={r}>{r.replace(/_/g, ' ').replace(/\./g, ' → ')}</li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              {cta && (
                <button
                  onClick={startOnboarding}
                  disabled={working}
                  className={`px-4 py-2 ${theme.buttonPrimary} rounded-lg text-sm font-semibold disabled:opacity-50`}
                >
                  {working ? 'Opening Stripe…' : cta}
                </button>
              )}
              {status?.accountId && (
                <button
                  onClick={openDashboard}
                  disabled={working}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 border ${theme.border} ${theme.text} rounded-lg text-sm font-semibold disabled:opacity-50`}
                >
                  Manage on Stripe <ExternalLink size={13} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reconciliation, offered rather than hidden: a host who thinks a
          payment is missing can check for themselves. */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={runAudit}
          disabled={auditing}
          className={`inline-flex items-center gap-1.5 px-3 py-2 border ${theme.border} ${theme.text} rounded-lg text-sm font-semibold disabled:opacity-50`}
        >
          <ShieldCheck size={14} />
          {auditing ? 'Checking with Stripe…' : 'Check payments with Stripe'}
        </button>
        <p className={`text-xs ${theme.textMuted}`}>
          Finds payments Stripe took that never reached us.
        </p>
      </div>

      {auditResult && (
        <div className={`${theme.card} rounded-xl p-4 space-y-2`}>
          <p className={`text-sm font-semibold ${theme.text}`}>
            Checked {auditResult.checked} payment{auditResult.checked === 1 ? '' : 's'} against Stripe
          </p>
          {auditResult.settled.length > 0 && (
            <p className="text-sm text-green-600">
              {auditResult.settled.length} found paid in Stripe and now marked paid.
            </p>
          )}
          {auditResult.stillUnpaid.length > 0 && (
            <p className={`text-sm ${theme.textSecondary}`}>
              {auditResult.stillUnpaid.length} genuinely unpaid — Stripe has no completed payment for them.
            </p>
          )}
          {auditResult.needsReview.length > 0 && (
            <div className="text-sm text-amber-700 dark:text-amber-400">
              <p className="font-semibold">
                {auditResult.needsReview.length} need review — logged rather than guessed at:
              </p>
              <ul className="list-disc pl-5 mt-1 space-y-0.5 text-xs">
                {auditResult.needsReview.map((r, i) => (
                  <li key={i}>
                    Registration {(r.registrationIds || []).join(', ')} — {String(r.reason || 'unclear').replace(/_/g, ' ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {auditResult.checked === 0 && (
            <p className={`text-sm ${theme.textSecondary}`}>Nothing outstanding to check.</p>
          )}
        </div>
      )}

      {/* The ledger lives in its own component: it paginates, filters and
          exports independently of everything else on this screen. */}
      {lines.length > 0 && (
        <TransactionsTable
          ownerType={ownerType}
          ownerId={ownerId}
          newLineCount={newLineCount}
          onGenerateStatement={generate}
          statementDisabledReason={
            !consented ? 'Agree to the statement note below first'
              : newLineCount === 0 ? `Nothing new since ${lastStatementNumber}`
              : null
          }
        />
      )}

      {/* Self-billing agreement. Kynaix raises the document in the host's name,
          which a supplier is normally required to have agreed to first. */}
      {lines.length > 0 && (
        <label className={`flex items-start gap-2.5 text-xs ${theme.textSecondary} cursor-pointer`}>
          <input
            type="checkbox"
            checked={consented}
            disabled={consented}
            onChange={giveConsent}
            className="mt-0.5 w-4 h-4 flex-shrink-0"
          />
          <span>
            I authorise Kynaix to raise statements of account on my behalf for
            entry fees collected for me.
            {consented && status?.selfBillingConsentAt && (
              <span className={theme.textMuted}>
                {' '}Agreed {new Date(status.selfBillingConsentAt).toLocaleDateString()}.
              </span>
            )}
          </span>
        </label>
      )}

      {statements.length > 0 && (
        <div className={`${theme.card} rounded-xl p-4`}>
          <h4 className={`text-sm font-bold ${theme.text} mb-3`}>Statements issued</h4>
          <ul className="space-y-2">
            {statements.map(st => (
              <li key={st.number} className="flex items-center justify-between gap-3 text-sm">
                <button
                  onClick={() => openStatement(st.number)}
                  disabled={opening === st.number}
                  className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <Download size={12} />
                  {opening === st.number ? 'Opening…' : st.number}
                </button>
                <span className={`text-xs ${theme.textMuted}`}>
                  {st.periodFrom && st.periodTo
                    ? `${new Date(st.periodFrom).toLocaleDateString()} – ${new Date(st.periodTo).toLocaleDateString()}`
                    : new Date(st.issuedAt).toLocaleDateString()}
                  {' · '}{st.lineCount} line{st.lineCount === 1 ? '' : 's'} · closing {money(st.owed)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showRequest && (
        <RequestPayoutModal
          ownerType={ownerType}
          ownerId={ownerId}
          owed={owed}
          onClose={() => setShowRequest(false)}
          onCreated={load}
        />
      )}

      <p className={`text-xs ${theme.textMuted}`}>
        Kynaix never sees your bank details. Identity and payout information are
        held by Stripe, who verify them and pay you directly.
      </p>
    </div>
  );
}
